import { IoClose } from "solid-icons/io";
import { type JSX, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import coverTopBannerAsset from "@/assets/fido-cover.svg";
import cardBannerAsset from "@/assets/fido-square-image.svg?url";
import { usePostHog } from "@/hooks/usePostHog";
import { cn } from "@/libs/cn";

const FIDO_PARTNERSHIP_URL = "https://fidocommercialista.it/sevedemo" as const;

/**
 * Cover top banner (728x90) - Designed for placement at the top of pages.
 * Partnership promotion with FidoCommercialista for SeVedemo users.
 * Image on the left, discount CTA on the right.
 *
 * **The X is optional, and only the profile layout turns it on** (B1, B9): the calculators
 * mount the same banner without `onDismiss`, so they have no X and a close does not reach
 * them. The banner itself remembers nothing — the layout reads the close and decides
 * whether to mount it. A close is not tracked: only the click on the partner is.
 *
 * The X is a **sibling** of the link, after it in the focus order (EC-FB6): a button inside
 * an `<a>` is not valid HTML. `class` (the margin) goes on the wrapper, so a closed banner
 * takes its margin with it.
 *
 * Below `lg` the creative has no free corner — an X over it would cover the title or the
 * coupon — so the X is a 44px column beside the banner (B3). From `lg` it sits in the
 * corner of the blue panel, which leaves room for it. The mark is white on a hairline,
 * fixed colours because the creative does not follow the theme: ~17:1 for the X on white,
 * 7.4:1 for the white edge on `#4044C4`. Nothing animates: it goes in the same frame.
 */
export function CoverTopBanner(props: {
  class?: string;
  onDismiss?: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const { capture } = usePostHog();
  const handleClick = () => {
    capture("click_partnership_fidocommercialista", {
      banner_type: "cover_top_banner",
    });
  };
  return (
    <div class={cn("relative flex items-start", props.class)}>
      <a
        href={FIDO_PARTNERSHIP_URL}
        target="_blank"
        rel="noopener noreferrer sponsored"
        class="flex h-full min-w-0 flex-1 items-stretch gap-4 rounded-lg border bg-[#E1EBFE] p-0 lg:min-h-[90px]"
        aria-label={t("partnership.fido.link_aria_label")}
        onClick={handleClick}
      >
        <div class="w-full lg:max-w-[70%] h-full shrink-0">
          <img
            src={coverTopBannerAsset}
            alt={t("partnership.fido.banner_alt")}
            width={728}
            height={250}
            class="block w-full h-auto object-contain"
            fetchpriority="high"
          />
        </div>
        <div
          class={cn(
            "hidden lg:flex flex-1 flex-col justify-center gap-1 self-stretch rounded-r-lg bg-[#4044C4] px-4",
            props.onDismiss && "lg:pr-12",
          )}
        >
          <p class="text-md xl:text-xl text-white">
            {t("partnership.fido.partner_message")}
          </p>
          <p class="text-2xl font-bold text-white">
            {t("partnership.fido.promotion_message")}
          </p>
        </div>
      </a>
      <Show when={props.onDismiss}>
        <button
          type="button"
          aria-label={t("partnership.fido.close_aria_label")}
          class="group flex size-11 shrink-0 cursor-pointer items-center justify-center outline-none lg:absolute lg:top-0 lg:right-0"
          onClick={() => props.onDismiss?.()}
        >
          <span class="flex size-5 items-center justify-center rounded-full bg-white text-slate-900 ring-1 ring-slate-900/15 group-hover:bg-slate-100 group-focus-visible:ring-2 group-focus-visible:ring-slate-900 group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-white lg:size-6">
            <IoClose size={12} aria-hidden="true" />
          </span>
        </button>
      </Show>
    </div>
  );
}

/**
 * Card banner (300x250) - Medium rectangle format for sidebar or card placement.
 * Partnership promotion with FidoCommercialista for SeVedemo users.
 * Includes title and description stating SeVedemo users are entitled to this discount.
 */
export function CardBanner(props: { class?: string }): JSX.Element {
  const { t } = useI18n();
  const { capture } = usePostHog();
  const handleClick = () => {
    capture("click_partnership_fidocomercialista", {
      banner_type: "card_banner",
    });
  };
  return (
    <a
      href={FIDO_PARTNERSHIP_URL}
      target="_blank"
      rel="noopener noreferrer sponsored"
      class={props.class}
      aria-label={t("partnership.fido.link_aria_label")}
      onClick={handleClick}
    >
      <div class="flex flex-col">
        <div class="flex flex-1 flex-col justify-center gap-1 self-stretch rounded-t-lg bg-[#4044C4] p-4">
          <p class="text-sm lg:text-md xl:text-xl text-white">
            {t("partnership.fido.partner_message")}
          </p>
          <p class="text-xl lg:text-2xl font-bold text-white">
            {t("partnership.fido.promotion_message")}
          </p>
        </div>
        <img
          src={cardBannerAsset}
          alt={t("partnership.fido.banner_alt")}
          width={300}
          height={250}
          class="block w-full h-auto object-contain"
          fetchpriority="low"
        />
      </div>
    </a>
  );
}
